<script setup>
// 미디어 허브 푸터 — 반응형.
//
// 원본: Figma 324:17353 (1920x411), 배경 검정
//   콘텐츠 x 64~1856 (1792)
//   설명문   y 63   18px 행간 26 흰색 (두 줄)
//   메뉴     y 63   오른쪽 끝, 18px semibold, 항목 간격 50
//   구분선   y 152  1792x1 흰색 30%
//   회사정보 y 183  두 덩이(x 64 / x 517), 15px 행간 22, 줄 간격 6
//                  항목 사이 1x12 흰색 30% 구분자, 간격 10
//   소셜     y 281  30x30 네 개, 간격 24, 오른쪽 끝
//   Copyright y 291 14px 행간 20 흰색 50%
//
// 홈페이지 푸터(homepage/src/components/home2/FooterSection.vue)와 같은 판이다.
// 다른 것은 배경색(검정)과 문구(REALGOLD · 미디어허브 설명)뿐이다.
// 고칠 때는 두 곳을 같이 볼 것.
//
// 원본에서 바꾼 것
//   1) 절대좌표를 걷어내고 가로 줄로 담았다. 좁아지면 접힌다.
//   2) 메뉴는 SITE_MENU 를 그대로 그린다 — 상단 메뉴와 같은 표다.
//      (예전에는 퍼블리싱의 <p> 6개를 useSiteFooter 가 그 자리에서 <a> 로 갈아 끼웠다)
//   3) 글자는 전부 원본 값 그대로 고정이다. 18 / 15 / 14px 이라 창 폭을 탈 이유가 없다.
import { ref } from 'vue'
import { useDesignScale } from '@/composables/useDesignScale.js'
import { SITE_MENU } from '@/data/siteMenu.js'
import imgSocialYouTube from '@/assets/figma/91ed637c86979b1a77c0c92f50413f3b509c6061.svg'
import imgSocialFacebook from '@/assets/figma/91db2067d123c43be29c48e9fe466bb2eff3a7a1.svg'
import imgSocialX from '@/assets/figma/e9f7a66e504a104007dece3962a79629e19fa17b.svg'
import imgSocialInstagram from '@/assets/figma/a46be4fab735b5f3c3c3f0967ecf2961c7e444a2.svg'

// 세로 리듬 배율. 여백을 창 폭에 맞춰 줄인다 — 글자는 건드리지 않는다.
const root = ref(null)
const { scale: rhythm } = useDesignScale(root)

const INFO_LEFT = [
  ['AURA', 'CEO', '배재한'],
  ['Business Registration No.', '573-81-03483'],
  ['Email', 'goldnara4@gmail.com'],
]
const INFO_RIGHT = [
  ['Address', '서울 종로구 돈화문로5가길 1 피카디리플러스 7층 701호'],
  ['골드나라 배재한의 금토크', '경남 창원시 성산구 원이대로 320 시티세븐 W145'],
  ['Number', '1833-5977'],
]

const SOCIALS = [
  { key: 'youtube', src: imgSocialYouTube, label: 'YouTube' },
  { key: 'facebook', src: imgSocialFacebook, label: 'Facebook' },
  { key: 'x', src: imgSocialX, label: 'X' },
  { key: 'instagram', src: imgSocialInstagram, label: 'Instagram' },
]
</script>

<template>
  <footer
    ref="root"
    :style="{ '--r': rhythm }"
    class="w-full bg-black pb-[calc(var(--r)*100px)] pt-[calc(var(--r)*63px)]"
    data-node-id="324:17353"
  >
    <div class="mx-auto w-full max-w-[1530px] px-[20px]">
      <!-- 설명문(왼쪽) + 메뉴(오른쪽) -->
      <div class="flex flex-wrap items-start justify-between gap-x-[calc(var(--r)*50px)] gap-y-[calc(var(--r)*24px)]">
        <p class="mf-lead" data-node-id="324:17356">
          골드나라 미디어허브에서
          <br aria-hidden />
          금나라의 주요 미디어 콘텐츠를 한 곳에서 확인하세요.
        </p>
        <nav class="flex flex-wrap gap-x-[50px] gap-y-[8px]" data-node-id="324:17357">
          <a v-for="item in SITE_MENU" :key="item.key" :href="item.href" class="mf-menu">
            {{ item.label }}
          </a>
        </nav>
      </div>

      <div class="mt-[calc(var(--r)*37px)] h-px w-full bg-white/30" data-node-id="324:17364" />

      <!-- 회사정보 두 덩이 -->
      <div class="mt-[calc(var(--r)*31px)] flex flex-wrap gap-x-[calc(var(--r)*159px)] gap-y-[calc(var(--r)*12px)]">
        <div v-for="(block, b) in [INFO_LEFT, INFO_RIGHT]" :key="b" class="flex flex-col gap-[6px]">
          <div v-for="(row, r) in block" :key="r" class="flex flex-wrap items-center gap-[10px]">
            <template v-for="(cell, c) in row" :key="c">
              <span v-if="c" aria-hidden class="h-[12px] w-px shrink-0 bg-white/30" />
              <span class="mf-info">{{ cell }}</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Copyright(왼쪽) + 소셜(오른쪽) -->
      <div class="mt-[calc(var(--r)*20px)] flex flex-wrap items-end justify-between gap-[calc(var(--r)*20px)]">
        <p class="mf-copy" data-node-id="324:17355">
          Copyright © AURA. All rights reserved.
        </p>
        <div class="flex gap-[24px]" data-node-id="324:17393">
          <span v-for="s in SOCIALS" :key="s.key" class="block size-[30px] shrink-0">
            <img :src="s.src" :alt="s.label" class="block size-full" />
          </span>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
/* 글자는 전부 원본 값 그대로 고정이다. 창 폭을 타지 않는다. */
.mf-lead {
  margin: 0;
  font-size: 18px;
  line-height: 26px;
  color: #fff;
  word-break: keep-all;
}

.mf-menu {
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #fff;
  white-space: nowrap;
}

.mf-info {
  font-size: 15px;
  line-height: 22px;
  color: #fff;
  word-break: keep-all;
}

.mf-copy {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #fff;
  opacity: 0.5;
  white-space: nowrap;
}
</style>
